import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Skeleton,
  Alert,
  Paper,
  ButtonGroup,
  TextField,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { useDatasetImport } from './useDatasetImport';
import { DATA_IMPORT_MODES } from '../../constants';

interface DatasetImportProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

interface AfterUploadSectionProps {
  handleRemoveFile: () => void;
  fileName: string;
}
const AfterUploadSection = ({ handleRemoveFile, fileName }: AfterUploadSectionProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: theme.spacing(2),
        px: theme.spacing(4),
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: '#f5f7fb',
      }}
    >
      <Typography variant="body1" sx={{ flex: 1 }}>
        {fileName}
      </Typography>
      <IconButton onClick={handleRemoveFile} size="small" sx={{ color: 'error.main' }}>
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

interface UploadFileSectionProps {
  handleClick: () => void;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  isUploadFileLoading: boolean;
  uploadDescription: string;
  inputRef: React.RefObject<HTMLInputElement>;
  handleUploadFile: (file: File | null) => void;
}

const UploadFileSection = ({
  handleClick,
  handleDrop,
  isUploadFileLoading,
  uploadDescription,
  inputRef,
  handleUploadFile,
}: UploadFileSectionProps) => {
  const theme = useTheme();
  return (
    <Box
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      sx={{
        border: '2px dashed',
        borderColor: theme.palette.divider,
        '&:hover': {
          borderColor: theme.palette.primary.light,
        },
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isUploadFileLoading ? (
        <Skeleton variant="text" width="100%" height={20} />
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={1}>
            <FileUploadIcon />
            <Typography variant="body1" color="text.secondary">
              {uploadDescription}
            </Typography>
          </Box>
          <input
            hidden
            type="file"
            accept=".parquet"
            ref={inputRef}
            onChange={(event) => handleUploadFile(event.target.files?.[0] ?? null)}
          />
        </>
      )}
    </Box>
  );
};

export const DatasetImport = ({ setRequiredFieldsFilled }: DatasetImportProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    uploadedNotesParquetFileName,
    uploadedPersonParquetFileName,
    uploadedVisitParquetFileName,
    handleRemoveNotesFile,
    handleRemovePersonFile,
    handleRemoveVisitFile,
    handleNotesClick,
    handlePersonClick,
    handleVisitClick,
    handleNotesDrop,
    handlePersonDrop,
    handleVisitDrop,
    isUploadNotesParquetFileLoading,
    isUploadPersonParquetFileLoading,
    isUploadVisitParquetFileLoading,
    inputNotesRef,
    inputPersonRef,
    inputVisitRef,
    handleUploadNotesParquetFile,
    handleUploadPersonParquetFile,
    handleUploadVisitParquetFile,
    uploadNotesError,
    uploadPersonError,
    uploadVisitError,
    datasetImportMode,
    setDataImportMode,
    uploadedNotesS3FilePath,
    uploadedPersonS3FilePath,
    uploadedVisitS3FilePath,

    handleSetNotesS3Path,
    handleSetPersonS3Path,
    handleSetVisitS3Path,

    notesS3Error,
    personS3Error,
    visitS3Error,

    isSetNotesS3FilePathLoading,
    isSetPersonS3FilePathLoading,
    isSetVisitS3FilePathLoading,

    notesS3Input,
    personS3Input,
    visitS3Input,
    setNotesS3Input,
    setPersonS3Input,
    setVisitS3Input,
    handleRemoveNotesS3Path,
    handleRemovePersonS3Path,
    handleRemoveVisitS3Path,
  } = useDatasetImport({ setRequiredFieldsFilled });

  return (
    <Paper
      sx={{
        p: theme.spacing(6),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
      }}
    >
      <Typography variant="h6">
        {formatMessage({ id: 'createProjectWizard.step.datasetImport.title' })}
      </Typography>

      <ButtonGroup
        color="inherit"
        orientation="horizontal"
        variant="contained"
        sx={{ borderRadius: 0, m: theme.spacing(2), width: '100%' }}
      >
        {DATA_IMPORT_MODES.map((mode) => {
          const isActive = datasetImportMode === mode;

          return (
            <Button
              key={mode}
              id={`${mode}-dataset`}
              displayLabel={formatMessage({
                id: `createProjectWizard.step.datasetImport.importMode.${mode}`,
              })}
              variant="contained"
              onClickHandler={() => setDataImportMode(mode)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                flex: 1,
                backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                color: isActive ? theme.palette.white.main : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.grey[300],
                },
                '&:focus': {
                  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.grey[200],
                },
              }}
            />
          );
        })}
      </ButtonGroup>

      {datasetImportMode == DATA_IMPORT_MODES[0] ? (
        <>
          {/* Upload notes file */}
          <Box
            sx={{
              p: theme.spacing(4),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Typography variant="h6">
                {formatMessage({ id: 'createProjectWizard.step.datasetImport.notes.header.title' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.main', alignSelf: 'start' }}>
                *
              </Typography>
            </Box>

            {uploadedNotesParquetFileName ? (
              <AfterUploadSection
                handleRemoveFile={handleRemoveNotesFile}
                fileName={uploadedNotesParquetFileName}
              />
            ) : (
              <UploadFileSection
                handleClick={handleNotesClick}
                handleDrop={handleNotesDrop}
                isUploadFileLoading={isUploadNotesParquetFileLoading}
                uploadDescription={formatMessage({
                  id: 'createProjectWizard.step.datasetImport.description',
                })}
                inputRef={inputNotesRef}
                handleUploadFile={handleUploadNotesParquetFile}
              />
            )}
            {uploadNotesError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadNotesError}
              </Alert>
            )}
          </Box>

          {/* Upload person file */}
          <Box
            sx={{
              p: theme.spacing(4),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Typography variant="h6">
                {formatMessage({
                  id: 'createProjectWizard.step.datasetImport.person.header.title',
                })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.main', alignSelf: 'start' }}>
                *
              </Typography>
            </Box>
            {uploadedPersonParquetFileName ? (
              <AfterUploadSection
                handleRemoveFile={handleRemovePersonFile}
                fileName={uploadedPersonParquetFileName}
              />
            ) : (
              <UploadFileSection
                handleClick={handlePersonClick}
                handleDrop={handlePersonDrop}
                isUploadFileLoading={isUploadPersonParquetFileLoading}
                uploadDescription={formatMessage({
                  id: 'createProjectWizard.step.datasetImport.description',
                })}
                inputRef={inputPersonRef}
                handleUploadFile={handleUploadPersonParquetFile}
              />
            )}
            {uploadPersonError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadPersonError}
              </Alert>
            )}
          </Box>

          {/* Upload visit file */}
          <Box
            sx={{
              p: theme.spacing(4),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Typography variant="h6">
                {formatMessage({ id: 'createProjectWizard.step.datasetImport.visit.header.title' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.main', alignSelf: 'start' }}>
                *
              </Typography>
            </Box>
            {uploadedVisitParquetFileName ? (
              <AfterUploadSection
                handleRemoveFile={handleRemoveVisitFile}
                fileName={uploadedVisitParquetFileName}
              />
            ) : (
              <UploadFileSection
                handleClick={handleVisitClick}
                handleDrop={handleVisitDrop}
                isUploadFileLoading={isUploadVisitParquetFileLoading}
                uploadDescription={formatMessage({
                  id: 'createProjectWizard.step.datasetImport.description',
                })}
                inputRef={inputVisitRef}
                handleUploadFile={handleUploadVisitParquetFile}
              />
            )}
            {uploadVisitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadVisitError}
              </Alert>
            )}
          </Box>
        </>
      ) : datasetImportMode == DATA_IMPORT_MODES[1] ? (
        <>
          {/* NOTES S3 */}
          <Box sx={{ p: theme.spacing(4), display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              {formatMessage({
                id: 'createProjectWizard.step.datasetImport.notes.s3.header.title',
              })}
            </Typography>

            {uploadedNotesS3FilePath ? (
              <AfterUploadSection
                fileName={uploadedNotesS3FilePath}
                handleRemoveFile={() => {
                  setNotesS3Input('');
                  handleRemoveNotesS3Path();
                }}
              />
            ) : (
              <Box display="flex" gap={2}>
                <TextField
                  type="text"
                  size="small"
                  value={notesS3Input}
                  placeholder={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.notes.s3.placeholder',
                  })}
                  sx={{ flex: 1 }}
                  onChange={(e) => setNotesS3Input(e.target.value)}
                  disabled={isSetNotesS3FilePathLoading}
                />

                <Button
                  id="save-notes-s3"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.s3.upload.button.label',
                  })}
                  onClickHandler={() => handleSetNotesS3Path(notesS3Input)}
                  disabled={!notesS3Input || isSetNotesS3FilePathLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Box>
            )}

            {notesS3Error && <Alert severity="error">{notesS3Error}</Alert>}
          </Box>

          {/* PERSON S3 */}
          <Box sx={{ p: theme.spacing(4), display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              {formatMessage({
                id: 'createProjectWizard.step.datasetImport.person.header.title',
              })}
            </Typography>
            {uploadedPersonS3FilePath ? (
              <AfterUploadSection
                fileName={uploadedPersonS3FilePath}
                handleRemoveFile={() => {
                  setPersonS3Input('');
                  handleRemovePersonS3Path();
                }}
              />
            ) : (
              <Box display="flex" gap={2}>
                <TextField
                  type="text"
                  size="small"
                  value={personS3Input}
                  placeholder={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.person.s3.placeholder',
                  })}
                  sx={{ flex: 1 }}
                  disabled={isSetPersonS3FilePathLoading}
                  onChange={(e) => setPersonS3Input(e.target.value)}
                />

                <Button
                  id="save-person-s3"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.s3.upload.button.label',
                  })}
                  onClickHandler={() => handleSetPersonS3Path(personS3Input)}
                  disabled={!personS3Input || isSetPersonS3FilePathLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Box>
            )}

            {personS3Error && <Alert severity="error">{personS3Error}</Alert>}
          </Box>

          {/* VISIT S3 */}
          <Box sx={{ p: theme.spacing(4), display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              {formatMessage({
                id: 'createProjectWizard.step.datasetImport.visit.header.title',
              })}
            </Typography>

            {uploadedVisitS3FilePath ? (
              <AfterUploadSection
                fileName={uploadedVisitS3FilePath}
                handleRemoveFile={() => {
                  setVisitS3Input('');
                  handleRemoveVisitS3Path();
                }}
              />
            ) : (
              <Box display="flex" gap={2}>
                <TextField
                  type="text"
                  size="small"
                  value={visitS3Input}
                  placeholder={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.visit.s3.placeholder',
                  })}
                  sx={{ flex: 1 }}
                  disabled={isSetVisitS3FilePathLoading}
                  onChange={(e) => setVisitS3Input(e.target.value)}
                />

                <Button
                  id="save-visit-s3"
                  displayLabel={formatMessage({
                    id: 'createProjectWizard.step.datasetImport.s3.upload.button.label',
                  })}
                  onClickHandler={() => handleSetVisitS3Path(visitS3Input)}
                  disabled={!visitS3Input || isSetVisitS3FilePathLoading}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </Box>
            )}

            {visitS3Error && <Alert severity="error">{visitS3Error}</Alert>}
          </Box>
        </>
      ) : (
        <> </>
      )}
    </Paper>
  );
};
